import React, { forwardRef, memo, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/react/dashboard';
import AwsS3, { type AwsS3UploadParameters } from '@uppy/aws-s3';
import ImageEditor from '@uppy/image-editor';
import Compressor from '@uppy/compressor';
import '@uppy/core/css/style.css';
import '@uppy/dashboard/css/style.css';
import '@uppy/image-editor/css/style.css';
import Modal from '@lib/components/Modal/Modal';
import { captureException } from '@lib/utils/captureException';
import SupportiveMessage, {
  SupportiveMessageProps,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import {
  getMaxFileSize,
  imageSizeLimit,
  uppyCompressorOptions,
} from '@lib/enums/mimeTypes';
import './FileUploaderUppy.scss';
import './dashboard.css';
import { AttachmentValue, OnChangeMultiple, OnChangeSingle } from './types';
import { UploaderView } from './enums';
import getLocale from './utils/getLocale';
import getAttachmentVariables from './utils/getAttachmentVariables';
import * as s3 from './utils/s3';
import styles from './FileUploaderUppy.module.scss';

export * from './enums';
export * from './types';

const MULTIPART_THRESHOLD = 100 * 1024 * 1024;

interface Props {
  allowedFileTypes: string[];
  className?: string;
  maxFileSize?: number;
  maxImageDimensions?: { width: number; height: number };
  requireSquare?: boolean;
  maxNumberOfFiles?: number;
  note?: string;
  onChange: OnChangeSingle | OnChangeMultiple;
  openButton: ({
    onClick,
  }: {
    onClick: () => void;
  }) => React.ReactElement | null;
  openUploaderModalOnInit?: boolean;
  useUploadStrategy?: boolean;
  type: UploaderView;
  value?: AttachmentValue | AttachmentValue[];
  supportiveMessageProps?: SupportiveMessageProps;
}

const FileUploaderUppy = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    allowedFileTypes,
    className = '',
    maxFileSize = imageSizeLimit,
    maxImageDimensions,
    requireSquare,
    maxNumberOfFiles = 1,
    note,
    onChange,
    openButton,
    openUploaderModalOnInit = false,
    supportiveMessageProps,
    type,
    useUploadStrategy = true,
    value,
  } = props;
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language;
  const [modalOpen, setModalOpen] = useState(
    _isEmpty(value) ? openUploaderModalOnInit : false,
  );
  const [uppyInstance, setUppyInstance] = useState<Uppy<
    { s3Key?: string },
    Record<string, unknown>
  > | null>(null);

  const handleOpen = () => {
    // The Uppy instance persists across modal open/close (it's only
    // recreated when restrictions/type/strategy change) so an
    // already-completed upload from a previous open would otherwise still be
    // shown here - clear it so the dashboard always starts empty.
    uppyInstance?.clear();
    setModalOpen(true);
  };
  const handleClose = () => setModalOpen(false);

  // Read via refs inside the uppy-creation effect below so that new
  // onChange/value/maxImageDimensions/requireSquare references on every
  // parent re-render don't tear down the Uppy instance (which aborts any
  // in-flight multipart upload) - only the restriction/strategy/type
  // dependencies below should recreate it.
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const maxImageDimensionsRef = useRef(maxImageDimensions);
  const requireSquareRef = useRef(requireSquare);
  useEffect(() => {
    onChangeRef.current = onChange;
    valueRef.current = value;
    maxImageDimensionsRef.current = maxImageDimensions;
    requireSquareRef.current = requireSquare;
  });

  useEffect(() => {
    const restrictions = {
      maxFileSize,
      allowedFileTypes,
      maxNumberOfFiles,
    };
    const uppy = new Uppy<{ s3Key?: string }, Record<string, unknown>>({
      id: 'uppy',
      restrictions,
      autoProceed: false,
    })
      .use(Compressor, uppyCompressorOptions)
      .use(ImageEditor, {});

    if (useUploadStrategy) {
      uppy.use(AwsS3, {
        shouldUseMultipart: (file) => (file.size ?? 0) > MULTIPART_THRESHOLD,
        async getUploadParameters(file) {
          const contentType = file.type ?? 'application/octet-stream';
          const { url, key } = await s3.presignSingle(file.name, contentType);
          uppy.setFileMeta(file.id, { s3Key: key });
          return {
            method: 'PUT',
            url,
            headers: { 'Content-Type': contentType },
            fields: {},
          } satisfies AwsS3UploadParameters;
        },
        async createMultipartUpload(file) {
          const contentType = file.type ?? 'application/octet-stream';
          const { uploadId, key } = await s3.createMultipart(
            file.name,
            contentType,
          );
          uppy.setFileMeta(file.id, { s3Key: key });
          return { uploadId, key };
        },
        // We don't support resumable uploads; report no existing parts so
        // Uppy always uploads every chunk fresh.
        listParts: () => [],
        async signPart(_file, { uploadId, key, partNumber }) {
          const { url } = await s3.signPart(uploadId, key, partNumber);
          return { method: 'PUT', url } satisfies AwsS3UploadParameters;
        },
        completeMultipartUpload: (_file, { uploadId, key, parts }) =>
          s3.completeMultipart(uploadId, key, parts),
        abortMultipartUpload: (_file, { uploadId, key }) =>
          uploadId ? s3.abortMultipart(uploadId, key) : undefined,
      });
    }

    uppy.on('file-added', (file) => {
      const fileType = file.extension ? `.${file.extension}` : '';
      const maxAllowedSize = getMaxFileSize(fileType);
      if ((file.size ?? 0) > maxAllowedSize) {
        uppy.removeFile(file.id);
        uppy.info(
          t('file-add-validation-error', {
            name: file.name,
            size: maxAllowedSize / 1024 / 1024,
            type: fileType,
          }),
          'error',
          8000,
        );
        return;
      }
      const currentMaxImageDimensions = maxImageDimensionsRef.current;
      const currentRequireSquare = requireSquareRef.current;
      if (
        (currentMaxImageDimensions || currentRequireSquare) &&
        file.data instanceof Blob
      ) {
        const objectUrl = URL.createObjectURL(file.data);
        const img = new window.Image();
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          if (
            currentMaxImageDimensions &&
            (img.width > currentMaxImageDimensions.width ||
              img.height > currentMaxImageDimensions.height)
          ) {
            uppy.removeFile(file.id);
            uppy.info(
              t('image-dimension-validation-error', {
                width: currentMaxImageDimensions.width,
                height: currentMaxImageDimensions.height,
              }),
              'error',
              8000,
            );
            return;
          }
          if (currentRequireSquare && img.width !== img.height) {
            uppy.removeFile(file.id);
            uppy.info(t('image-square-validation-error'), 'error', 8000);
          }
        };
        img.src = objectUrl;
      }
    });

    uppy.on('complete', (result) => {
      const files = result.successful ?? [];
      const isSuccessful = files.length > 0;

      const currentOnChange = onChangeRef.current;
      const currentValue = valueRef.current;

      if (!useUploadStrategy) {
        const localFiles = files.map((file) => ({
          filename: file.name,
          size: file.size,
          mimeType: file.type,
          file: file.data as File,
        })) as AttachmentValue[];

        if (maxNumberOfFiles === 1) {
          (currentOnChange as OnChangeSingle)(localFiles[0]);
        } else {
          (currentOnChange as OnChangeMultiple)(localFiles);
        }

        if (type === UploaderView.modal) handleClose();
        return;
      }

      if (maxNumberOfFiles === 1) {
        const file = files[0];
        if (file?.id) {
          const newValue = getAttachmentVariables(file);
          (currentOnChange as OnChangeSingle)(newValue);
        }
      } else if (maxNumberOfFiles > 1) {
        const newValue: AttachmentValue[] = [];
        files.forEach((file) => {
          if (file?.id) {
            newValue.push(getAttachmentVariables(file));
          }
        });
        if (currentValue && Array.isArray(currentValue)) {
          (currentOnChange as OnChangeMultiple)([...currentValue, ...newValue]);
        } else {
          (currentOnChange as OnChangeMultiple)(newValue);
        }
      }
      if (type === UploaderView.modal && isSuccessful) {
        handleClose();
      }
    });

    uppy.on('upload-error', (file, error, response) => {
      // TODO: Handle error, maybe by showing an alert to the user

      console.error('Failed to upload', file?.name, error, response);

      const reportedError = new Error(error.message);
      reportedError.name = error.name;
      captureException(reportedError, {
        fileName: file?.name,
        fileSize: file?.size,
        details: error.details,
        context: (error as { context?: unknown }).context,
        response,
      });
    });

    setUppyInstance(uppy);

    return () => {
      uppy.destroy();
    };
    // onChange/value/maxImageDimensions/requireSquare are intentionally
    // excluded - they're read from refs above so that new references on
    // parent re-renders don't tear down the Uppy instance and abort any
    // in-flight multipart upload.
  }, [
    allowedFileTypes,
    maxFileSize,
    maxNumberOfFiles,
    t,
    type,
    useUploadStrategy,
  ]);

  if (uppyInstance === null) return null;

  const uppyDashboard = (
    <div
      className={classNames(styles.uppyRoot, className, {
        [styles.uppyRootWithMessage]: supportiveMessageProps,
      })}
    >
      <Dashboard
        uppy={uppyInstance}
        note={note}
        proudlyDisplayPoweredByUppy={false}
        plugins={['ImageEditor']}
        showSelectedFiles
        showRemoveButtonAfterComplete
        // Multipart uploads don't support pause/resume in our setup (see
        // `listParts` below) - resuming a paused multipart upload hits an
        // Uppy bug where already-completed chunks are re-requested as null.
        hidePauseResumeButton
        locale={getLocale(currentLocale)}
        // @ts-ignore TODO: fix this
        mobileNativeCamera
      />
      {supportiveMessageProps && (
        <div style={{ marginTop: '16px' }}>
          <SupportiveMessage
            color={supportiveMessageProps.color}
            text={supportiveMessageProps.text}
            actions={supportiveMessageProps.actions}
            fullWidth
          />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <input type="hidden" ref={ref} />
      {type === UploaderView.modal && (
        <>
          {openButton({ onClick: handleOpen })}
          <Modal
            isOpen={modalOpen}
            onClose={handleClose}
            title={maxNumberOfFiles > 1 ? t('upload-files') : t('upload-file')}
          >
            {uppyDashboard}
          </Modal>
        </>
      )}
      {type === UploaderView.dragDrop && uppyDashboard}
    </div>
  );
});

export default memo(FileUploaderUppy);
